import {
  Center,
  HStack,
  Link,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import {
  fieldStateForTierType,
  TierFieldName,
  TierFieldNames,
} from "features/waterfall/WaterfallTierFieldState";
import { localizeTierType } from "features/waterfall/WaterfallUtils";
import { useFundList } from "gen/endpoints/fund-entities/fund-entities";
import { useGlobalEntityList } from "gen/endpoints/global-entity/global-entity";
import { CashFlowEffectiveDateEnum } from "gen/model/cashFlowEffectiveDateEnum";
import { GlobalEntityListFunctionalType } from "gen/model/globalEntityListFunctionalType";
import { InterestCompoundingCadenceEnum } from "gen/model/interestCompoundingCadenceEnum";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { WaterfallCalculationTypeEnum } from "gen/model/waterfallCalculationTypeEnum";
import { WaterfallConfigCarryPartnerData } from "gen/model/waterfallConfigCarryPartnerData";
import { WaterfallConfigData } from "gen/model/waterfallConfigData";
import { WaterfallTierHurdlePercentageCreateData } from "gen/model/waterfallTierHurdlePercentageCreateData";
import { WaterfallTierTypeEnum } from "gen/model/waterfallTierTypeEnum";
import { WaterfallTypeEnum } from "gen/model/waterfallTypeEnum";
import { isEmpty } from "lodash-es";
import React, { useCallback, useEffect } from "react";
import {
  DefaultValues,
  useFieldArray,
  useForm,
  useFormContext,
  UseFormReturn,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import {
  FormBooleanToggleButtonGroup,
  FormToggleButtonGroup,
  ToggleButton,
} from "shared/components/button/ToggleButton";
import {
  ContentBlock,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockTitle,
} from "shared/components/content/ContentBlock";
import Form from "shared/components/form/Form";
import {
  FormFooter,
  FormFooterActions,
} from "shared/components/form/FormFooter";
import { FormHiddenInput } from "shared/components/form/FormHiddenInput";
import { FormUnsavedIndicator } from "shared/components/form/FormUnsavedIndicator";
import { FormDateInput } from "shared/components/input/date/DateInput";
import { FormSelect } from "shared/components/input/select/Select";
import {
  SelectElement,
  SelectGroup,
  SelectOption,
} from "shared/components/input/select/SelectOption";
import { FormSimpleSelect } from "shared/components/input/select/SimpleSelect";
import { FormNumberInput } from "shared/components/input/text/NumberInput";
import { FormSimpleNumberInput } from "shared/components/input/text/SimpleNumberInput";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxCollapse } from "shared/components/table/box/TableBoxCollapse";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import {
  TableAddButton,
  TableRemoveButton,
} from "shared/components/table/TableRowActionButtons";
import { enumKeys } from "shared/utils/EnumUtils";
import { useHasPermission } from "shared/utils/PermissionUtils";

export interface WaterfallEditFormProps {
  fundFamilyId: string;
  waterfallData: WaterfallConfigData | undefined;
  saveWaterfallConfig: (data: WaterfallConfigData) => Promise<void>;
  onCancel?: () => void;
  isDisabled?: boolean;
  additionalSections?: React.ReactNode;
}

export const WaterfallEditForm: React.FC<WaterfallEditFormProps> = ({
  fundFamilyId,
  waterfallData,
  saveWaterfallConfig,
  onCancel,
  isDisabled,
  additionalSections,
}) => {
  const { t } = useTranslation(["enums", "common", "waterfall"]);
  const hasConfigPermission = useHasPermission(PermissionTypeEnum.config);

  const { data: entitiesData } = useFundList({
    fund_family_ids: [fundFamilyId],
  });

  const form = useForm<
    WaterfallConfigData & {
      pref_rate_varies: boolean;
    }
  >({
    defaultValues: {
      ...waterfallData,
      pref_rate_varies:
        waterfallData?.tiers?.some(
          (tierData) =>
            tierData.hurdle_percentages &&
            tierData.hurdle_percentages.length > 1,
        ) || false,
    } as DefaultValues<WaterfallConfigData>,
  });

  const waterfallCalcType = form.watch("waterfall_calc_type");

  const {
    fields: carryPartners,
    append: addCarryPartner,
    remove: removeCarryPartner,
  } = useFieldArray({ control: form.control, name: "carry_partner_data" });

  const {
    fields: tiers,
    append: addTier,
    remove: removeTier,
  } = useFieldArray({ control: form.control, name: "tiers" });

  const onClickAddCarryPartner = useCallback(() => {
    addCarryPartner({} as WaterfallConfigCarryPartnerData);
  }, [addCarryPartner]);

  const onClickTierAdd = useCallback(() => {
    addTier({
      dataclass_name: "WaterfallTierData",
      id: null,
      tier_name: "",
      tier_order: 0,
      tier_type: "" as never,
      waterfall_config_id: null,
      hurdle_percentages: [],
      catch_up_percentage: "0",
      carry_partner_percentage: "0",
      participating_partner_percentage: "0",
    });
  }, [addTier]);

  const onClickTierRemove = useCallback(
    (index: number) => {
      removeTier(index);
      if (tiers.length === 1) {
        onClickTierAdd();
      }
    },
    [onClickTierAdd, removeTier, tiers],
  );

  const onSubmit = useCallback(
    async (data: WaterfallConfigData) => {
      await saveWaterfallConfig(data);
      return data;
    },
    [saveWaterfallConfig],
  );

  const prefRateVaries = form.watch("pref_rate_varies");
  const tiersState = form.watch("tiers");

  useEffect(() => {
    tiersState?.forEach((tier, index) => {
      // Backend expects tier name in addition to type.
      // For now this is non-user-editable, so set to the localized string.
      const tierName = localizeTierType(t, tier.tier_type);
      if (tierName && tier.tier_name !== tierName) {
        form.setValue(`tiers.${index}.tier_name`, tierName);
      }

      // Backend expects tier_order (doesn't rely on array order).
      const tierOrder = index + 1;
      if (tier.tier_order !== tierOrder) {
        form.setValue(`tiers.${index}.tier_order`, tierOrder);
      }

      // Clear any fields that are not relevant for the tier type.
      for (const fieldName of TierFieldNames) {
        const fieldState = fieldStateForTierType[tier.tier_type];
        const currentValue = tier[fieldName];
        const state = fieldState?.[fieldName];
        if (
          state &&
          currentValue !== "0" &&
          fieldName !== "hurdle_percentages"
        ) {
          form.setValue(`tiers.${index}.${fieldName}`, "0");
        }
        if (
          fieldName === "hurdle_percentages" &&
          tier.tier_type !== "preferred_return" &&
          state &&
          !isEmpty(currentValue)
        ) {
          form.setValue(`tiers.${index}.${fieldName}`, []);
        }
      }
    });
  }, [form, tiersState, tiers, t, addTier]);

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      isDisabled={isDisabled ?? !hasConfigPermission}
      successMessage={t("common:saved")}
    >
      <ContentBlockGroup>
        <ContentBlock>
          <ContentBlockHeader>
            <ContentBlockTitle>
              {t("waterfall:waterfallSettings")}
            </ContentBlockTitle>
          </ContentBlockHeader>
          <ContentBlockBody variant="form">
            <FormSelect
              name="entity_id"
              title={t("waterfall:waterfallForFamilyOrEntity")}
              unsetWhenEmpty={true}
            >
              <SelectOption value="">{t("common:fundFamily")}</SelectOption>
              <SelectGroup label={t("common:entities")}>
                {entitiesData?.map(({ id, legal_name }) => (
                  <SelectOption key={`entity_${id}`} value={id}>
                    {legal_name}
                  </SelectOption>
                ))}
              </SelectGroup>
            </FormSelect>
            <FormToggleButtonGroup
              name="waterfall_type"
              title={t("waterfall:waterfallType.title")}
            >
              <ToggleButton value={WaterfallTypeEnum.whole_fund}>
                {t("waterfall:waterfallType.wholeFund")}
              </ToggleButton>
              <ToggleButton value={WaterfallTypeEnum.hybrid}>
                {t("waterfall:waterfallType.hybrid")}
              </ToggleButton>
            </FormToggleButtonGroup>
          </ContentBlockBody>
        </ContentBlock>
        <ContentBlock>
          <ContentBlockHeader>
            <ContentBlockTitle>
              {t("waterfall:preferredReturns.title")}
            </ContentBlockTitle>
          </ContentBlockHeader>
          <ContentBlockBody variant="form">
            <FormToggleButtonGroup
              name="waterfall_calc_type"
              title={t("waterfall:preferredReturns.calculatedAs")}
            >
              <ToggleButton value={WaterfallCalculationTypeEnum.interest_rate}>
                {t("waterfall:calcType.interestRate")}
              </ToggleButton>
              <ToggleButton value={WaterfallCalculationTypeEnum.irr}>
                {t("waterfall:calcType.irr")}
              </ToggleButton>
            </FormToggleButtonGroup>
            {waterfallCalcType ===
              WaterfallCalculationTypeEnum.interest_rate && (
              <FormToggleButtonGroup
                name="interest_compounding_cadence"
                title={t("waterfall:preferredReturns.compounded")}
              >
                <ToggleButton value={InterestCompoundingCadenceEnum.daily}>
                  {t("waterfall:compoundingCadence.never")}
                </ToggleButton>
                <ToggleButton value={InterestCompoundingCadenceEnum.monthly}>
                  {t("waterfall:compoundingCadence.monthly")}
                </ToggleButton>
                <ToggleButton value={InterestCompoundingCadenceEnum.annually}>
                  {t("waterfall:compoundingCadence.annually")}
                </ToggleButton>
              </FormToggleButtonGroup>
            )}
            {waterfallCalcType === WaterfallCalculationTypeEnum.irr && (
              <FormToggleButtonGroup
                name="interest_compounding_cadence"
                title={t("waterfall:preferredReturns.compounded")}
              >
                <ToggleButton value={InterestCompoundingCadenceEnum.monthly}>
                  {t("waterfall:compoundingCadence.monthly")}
                </ToggleButton>
                <ToggleButton value={InterestCompoundingCadenceEnum.annually}>
                  {t("waterfall:compoundingCadence.annually")}
                </ToggleButton>
              </FormToggleButtonGroup>
            )}
            <CashFlowEffectiveDateFormSelect disabled={false} />
            <FormBooleanToggleButtonGroup
              name="pref_rate_varies"
              title={t("waterfall:preferredReturns.pref_hurdle_varies")}
            />
          </ContentBlockBody>
        </ContentBlock>
        <ContentBlock>
          <ContentBlockHeader>
            <ContentBlockTitle>
              {t("waterfall:carriedInterest")}
            </ContentBlockTitle>
          </ContentBlockHeader>
          <ContentBlockBody variant="form">
            <FormBooleanToggleButtonGroup
              name="additional_carried_interest_calculation"
              title={t("waterfall:additionalCarriedInterestCalculation")}
            />
            <FormBooleanToggleButtonGroup
              name="interim_clawback_on_carry"
              title={t("waterfall:interimClawbackOnCarry")}
            />
            <FormBooleanToggleButtonGroup
              name="special_gp_distributions_allowed"
              title={t("waterfall:specialGpDistributionsAllowed")}
            />
            {carryPartners.map((field, index) => (
              <HStack key={field.id} gap={2} alignItems="flex-start">
                <CarryPartnerSelect
                  name={`carry_partner_data.${index}.carry_partner_id`}
                />
                <FormNumberInput
                  name={`carry_partner_data.${index}.ownership_percentage`}
                  title={t("waterfall:carryPartner.allocation")}
                  valueType="percent"
                  controlProps={{
                    maxWidth: "145px",
                  }}
                />
                <Center height="48px">
                  <TableRemoveButton
                    onClick={() => {
                      removeCarryPartner(index);
                      if (carryPartners.length === 1) {
                        onClickAddCarryPartner();
                      }
                    }}
                  />
                </Center>
              </HStack>
            ))}
            {!isDisabled && (
              <Link onClick={onClickAddCarryPartner}>
                {t("waterfall:carryPartner.addAdditional")}
              </Link>
            )}
          </ContentBlockBody>
        </ContentBlock>
      </ContentBlockGroup>
      <TableBox isDefaultExpanded={true}>
        <TableBoxHeader>
          <TableBoxTitle textStyle="bodyBold">
            {t("waterfall:setUpTiers")}
          </TableBoxTitle>
        </TableBoxHeader>
        <TableBoxBody>
          <TableBoxCollapse>
            <TableContainer width="100%" height="100%" overflowY="auto">
              <Table variant="form">
                <Thead>
                  <Tr>
                    <Th width="100%">{t("waterfall:tierType")}</Th>
                    <Th width="min-content">{t("waterfall:hurdle")}</Th>
                    <Th width="min-content">{t("waterfall:catchUp")}</Th>
                    <Th width="min-content">
                      {t("waterfall:carryPartner.title")}
                    </Th>
                    <Th width="min-content" colSpan={2}>
                      {t("waterfall:participatingPartner")}
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {tiers.map((field, index) => (
                    <WaterfallTier
                      key={field.id}
                      index={index}
                      onClickTierAdd={onClickTierAdd}
                      onClickTierRemove={onClickTierRemove}
                      prefRateVaries={prefRateVaries}
                    />
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </TableBoxCollapse>
        </TableBoxBody>
      </TableBox>
      {additionalSections}

      {!isDisabled && (
        <FormFooter>
          <FormUnsavedIndicator />
          <FormFooterActions>
            <FormCancelButton onClick={onCancel} />
            <FormSubmitButton>{t("common:saveProgress")}</FormSubmitButton>
          </FormFooterActions>
        </FormFooter>
      )}
    </Form>
  );
};

const CarryPartnerSelect: React.FC<{
  name: string;
}> = ({ name }) => {
  const { t } = useTranslation(["fundFamily", "waterfall"]);
  const { data } = useGlobalEntityList({
    functional_type: GlobalEntityListFunctionalType.general_partner,
  });
  return (
    <FormSelect title={t("waterfall:carryPartner.select")} name={name}>
      {data?.map(({ id, legal_name }) => (
        <SelectOption key={`partner_${id}`} value={id!}>
          {legal_name}
        </SelectOption>
      ))}
    </FormSelect>
  );
};

const WaterfallTier: React.FC<{
  index: number;
  onClickTierAdd: () => void;
  onClickTierRemove: (index: number) => void;
  prefRateVaries: boolean;
}> = ({ index, onClickTierAdd, onClickTierRemove, prefRateVaries }) => {
  const form = useFormContext<WaterfallConfigData>();
  const tierType = form.watch(
    `tiers.${index}.tier_type` as "tiers.0.tier_type",
  );
  return (
    <>
      <Tr>
        <Td>
          <HStack minWidth="200px">
            <TableAddButton onClick={onClickTierAdd} />
            <WaterfallTierTypeField index={index} />
          </HStack>
          <FormHiddenInput name={`tiers.${index}.tier_order`} />
        </Td>
        <Td>
          <WaterfallTierValueField
            index={index}
            name="hurdle_percentages"
            tierType={tierType}
            prefRateVaries={prefRateVaries}
          />
        </Td>
        <Td>
          <WaterfallTierValueField
            index={index}
            name="catch_up_percentage"
            tierType={tierType}
            prefRateVaries={prefRateVaries}
          />
        </Td>
        <Td>
          <WaterfallTierValueField
            index={index}
            name="carry_partner_percentage"
            tierType={tierType}
            prefRateVaries={prefRateVaries}
          />
        </Td>
        <Td>
          <WaterfallTierValueField
            index={index}
            name="participating_partner_percentage"
            tierType={tierType}
            prefRateVaries={prefRateVaries}
          />
        </Td>
        <Td>
          <TableRemoveButton onClick={() => onClickTierRemove(index)} />
        </Td>
      </Tr>
      {PreferredReturnHurdleRows({ form, index, prefRateVaries, tierType })}
    </>
  );
};

const PreferredReturnHurdleRows: React.FC<{
  form: UseFormReturn<WaterfallConfigData>;
  index: number;
  prefRateVaries: boolean;
  tierType: WaterfallTierTypeEnum;
}> = ({ form, index, prefRateVaries, tierType }) => {
  const {
    fields: prefHurdlePercentages,
    append: addPrefHurdlePercentages,
    remove: removePrefHurdlePercentages,
  } = useFieldArray({
    control: form.control,
    name: `tiers.${index}.hurdle_percentages` as "tiers.0.hurdle_percentages",
  });
  const { t } = useTranslation(["common"]);

  if (!prefRateVaries || tierType !== "preferred_return") {
    return null;
  }

  return (
    <>
      {prefHurdlePercentages.map((field, prefIndex) => (
        <Tr key={field.id}>
          <Td>
            <HStack minWidth="200px">
              <TableAddButton
                onClick={() => {
                  addPrefHurdlePercentages(
                    {} as WaterfallTierHurdlePercentageCreateData,
                  );
                }}
                alwaysVisible={prefHurdlePercentages.length === prefIndex + 1}
              />
              <FormDateInput
                name={`tiers.${index}.hurdle_percentages.${prefIndex}.start_date`}
                title={t("common:startDate")}
                showMessages={false}
                controlProps={{ pb: 0 }}
                shouldUnregister={false}
              />
              <FormDateInput
                name={`tiers.${index}.hurdle_percentages.${prefIndex}.end_date`}
                title={t("common:endDate")}
                showMessages={false}
                controlProps={{ pb: 0 }}
                shouldUnregister={false}
              />
            </HStack>
          </Td>
          <Td>
            <FormSimpleNumberInput
              name={`tiers.${index}.hurdle_percentages.${prefIndex}.hurdle_percentage`}
              valueType="percent"
              isDisabled={false}
              minWidth="100px"
              shouldUnregister={false}
            />
          </Td>
          <Td>
            <TableRemoveButton
              onClick={() => {
                removePrefHurdlePercentages(prefIndex);
                if (prefHurdlePercentages.length === 1) {
                  addPrefHurdlePercentages(
                    {} as WaterfallTierHurdlePercentageCreateData,
                  );
                }
              }}
            />
          </Td>
        </Tr>
      ))}
    </>
  );
};

const WaterfallTierValueField: React.FC<{
  index: number;
  name: TierFieldName;
  tierType: WaterfallTierTypeEnum;
  prefRateVaries: boolean;
}> = ({ index, name, tierType, prefRateVaries }) => {
  const fieldState = fieldStateForTierType[tierType]?.[name];
  if (
    prefRateVaries &&
    name === "hurdle_percentages" &&
    tierType === "preferred_return"
  ) {
    // If preferred return varies, don't show the hurdle percentage field for pref tiers
    // Instead, it'll show up as subrows
    return null;
  }
  const hiddenDateInputs =
    !prefRateVaries &&
    name === "hurdle_percentages" &&
    tierType === "preferred_return" ? (
      // If preferred return doesn't vary, we need to send the start and end date as null as hidden inputs
      <>
        <FormHiddenInput
          name={`tiers.${index}.${name}.0.start_date`}
          value={undefined}
        />
        <FormHiddenInput
          name={`tiers.${index}.${name}.0.end_date`}
          value={undefined}
        />
      </>
    ) : null;

  // Change fullName for hurdle_percentages since it's actually a list
  const fullName =
    name === "hurdle_percentages"
      ? `tiers.${index}.${name}.0.hurdle_percentage`
      : `tiers.${index}.${name}`;
  return (
    <>
      {hiddenDateInputs}
      <FormSimpleNumberInput
        name={fullName}
        valueType="percent"
        isDisabled={fieldState === "disabled"}
        isNotApplicable={fieldState === "not_applicable"}
        minWidth="100px"
      />
    </>
  );
};

const WaterfallTierTypeField: React.FC<{
  index: number;
}> = ({ index }) => {
  const { t } = useTranslation(["enums", "waterfall"]);
  return (
    <>
      <FormSimpleSelect
        title={t("waterfall:tierType")}
        name={`tiers.${index}.tier_type` as const}
      >
        {Object.values(WaterfallTierTypeEnum).map((type) => (
          <SelectOption key={type} value={type}>
            {localizeTierType(t, type)}
          </SelectOption>
        ))}
      </FormSimpleSelect>
      <FormHiddenInput name={`tiers.${index}.tier_name`} />
    </>
  );
};

const CashFlowEffectiveDateFormSelect: React.FC<{
  disabled: boolean;
}> = ({ disabled }) => {
  const { t } = useTranslation(["waterfall", "enums"]);

  const effectiveDateElements = enumKeys(
    CashFlowEffectiveDateEnum,
  ).map<SelectElement>((effectiveDate) => ({
    value: effectiveDate,
    rich_value: t(`enums:CashFlowEffectiveDateEnum.${effectiveDate}`),
  }));

  return (
    <FormSelect
      name="cash_flow_effective_date"
      title={t("waterfall:prefAccrualDate.title")}
      elements={effectiveDateElements}
      isDisabled={disabled}
    />
  );
};
